/**
 * The proof stage (INTAKE_GUIDED_FLOW §3.4 E; Allen ruled Q1 2026-10-03): World's walk on the EXACT
 * bytes the Fix step built, in an isolated stage laid over the live one. Grown from `portrait-stage.ts`,
 * which already "owns its renderer" and loads fresh export bytes in isolation; this one keeps a mixer
 * loop running and uses the live stage's lens constants, so it looks like the stage it covers.
 *
 * Isolated on purpose: the Document, the session subject and the live stage are untouched — a finger
 * graft survives ‹ Back, and no detector re-fires on a second subject (design §6 Q1).
 *
 * The binding is World's: the library clip's tracks by NAME onto the built file's nodes, absolute, with
 * World's hip-height rule (`bindLibraryClip`). The built bytes carry World's skeleton names, which is why
 * the walk binds here where the live stage's preview refuses a preset (ADR-040 §10).
 *
 * Depends on: three, adapter-three (library clip load + bind), lens.ts (constants, placement),
 * intake/first-frame.ts (the fit). Depended on by: main.ts (the session's `showWalk`).
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { bindLibraryClip, loadLibraryClip } from '@harryhausen/adapter-three';
import type { RigDescriptor } from '@harryhausen/kernel';
import { ENVIRONMENT_INTENSITY, KEY_DIRECTION, KEY_INTENSITY, lensPlacement } from './lens.js';
import { fitHeadFrame } from '../intake/first-frame.js';

export interface ProofStageProbe {
  readonly playing: boolean;
  readonly clip: string | null;
  readonly bound: number;
  readonly total: number;
  readonly seconds: number;
  readonly frames: number;
}

export interface ProofPlayInputs {
  /** The built file (the bytes the Fix step re-checked). */
  readonly glb: ArrayBuffer;
  /** World's walk clip bytes. */
  readonly clip: ArrayBuffer;
  /** The built file's rig (its hip rest feeds World's hip-height rule). */
  readonly rig: RigDescriptor;
  /** The ArcRotate azimuth the body faces (alphaForFacing). */
  readonly alphaRad: number;
  /** Where the step card's top edge is, in the host's pixels — the walker stays above it. */
  readonly cardTop: () => number;
}

/** A front three-quarter: the walk reads better a little off the axis. */
const THREE_QUARTER_RAD = (25 * Math.PI) / 180;

export function createProofStage(host: HTMLElement) {
  let renderer: THREE.WebGLRenderer | null = null;
  let stop: (() => void) | null = null;
  /** Each play takes a ticket; hide() or a newer play voids it, and a voided play never mounts (Codex P2, #242). */
  let ticket = 0;
  let probe: ProofStageProbe = { playing: false, clip: null, bound: 0, total: 0, seconds: 0, frames: 0 };
  /**
   * Shown only while a proof screen shows it (main's guided frame says): a walk left playing must never
   * cover the editor or take its clicks (Allen's rerun, 2026-10-03: "a companion rendering in the editor").
   */
  let visible = true;
  const applyVisible = (): void => {
    const c = renderer?.domElement;
    if (c !== undefined) c.style.display = visible ? '' : 'none';
  };

  const hide = (): void => {
    ticket += 1;
    stop?.();
    stop = null;
    probe = { ...probe, playing: false };
  };

  const play = async (inputs: ProofPlayInputs): Promise<ProofStageProbe> => {
    hide();
    const mine = ticket;
    const gltf = await new GLTFLoader().parseAsync(inputs.glb.slice(0), '');
    if (mine !== ticket) return probe;
    const library = await loadLibraryClip(inputs.clip.slice(0));
    if (mine !== ticket) return probe;
    const root = gltf.scene;
    const bound = bindLibraryClip(library.clip, root, { donor: library.hipRest, rig: inputs.rig });

    renderer ??= new THREE.WebGLRenderer({ antialias: true });
    const r = renderer;
    r.toneMapping = THREE.NeutralToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const canvas = r.domElement;
    canvas.className = 'proof-stage';
    canvas.dataset['testid'] = 'proof-stage';
    // OVER the live stage, not after it in the flow (Codex P1, #242): the host is the positioned viewport centre.
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.zIndex = '1';
    // It shows; it never takes input (the editor's stage is underneath).
    canvas.style.pointerEvents = 'none';
    host.append(canvas);
    applyVisible();

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);
    const pmrem = new THREE.PMREMGenerator(r);
    const room = new RoomEnvironment();
    const environment = pmrem.fromScene(room, 0.04);
    room.dispose();
    pmrem.dispose();
    scene.environment = environment.texture;
    scene.environmentIntensity = ENVIRONMENT_INTENSITY;
    scene.add(root);
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root);
    const key = new THREE.DirectionalLight(0xffffff, KEY_INTENSITY);
    scene.add(key, key.target);

    const camera = new THREE.PerspectiveCamera(30, 1, 0.01, 100);
    const frame = (): void => {
      const w = Math.max(1, host.clientWidth);
      const h = Math.max(1, host.clientHeight);
      r.setSize(w, h, false);
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      camera.aspect = w / h;
      const alpha = inputs.alphaRad + THREE_QUARTER_RAD;
      const lens =
        fitHeadFrame({ min: box.min, max: box.max }, alpha, { width: w, height: h, cardTop: inputs.cardTop() }, (30 * Math.PI) / 180, 0) ??
        fitHeadFrame({ min: box.min, max: box.max }, alpha, { width: w, height: h, cardTop: h }, (30 * Math.PI) / 180, 0);
      if (lens === null) return;
      const centre = box.getCenter(new THREE.Vector3());
      const at = lensPlacement(lens, { x: centre.x + KEY_DIRECTION.x * 4, y: centre.y + KEY_DIRECTION.y * 4, z: centre.z + KEY_DIRECTION.z * 4 });
      camera.fov = at.fovDeg;
      camera.near = at.near;
      camera.far = at.far;
      camera.position.set(at.camera.x, at.camera.y, at.camera.z);
      camera.lookAt(at.target.x, at.target.y, at.target.z);
      camera.updateProjectionMatrix();
      key.position.set(at.key.x, at.key.y, at.key.z);
      key.target.position.set(at.keyTarget.x, at.keyTarget.y, at.keyTarget.z);
      key.target.updateMatrixWorld();
    };
    frame();
    const resize = new ResizeObserver(frame);
    resize.observe(host);

    const mixer = new THREE.AnimationMixer(root);
    mixer.clipAction(bound.clip).play();
    const clock = new THREE.Clock();
    let running = true;
    probe = { playing: true, clip: library.clip.name, bound: bound.bound, total: bound.total, seconds: 0, frames: 0 };
    const tick = (): void => {
      if (!running) return;
      const dt = Math.min(clock.getDelta(), 0.1);
      mixer.update(dt);
      if (visible) r.render(scene, camera);
      probe = { ...probe, seconds: probe.seconds + dt, frames: probe.frames + 1 };
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    stop = () => {
      running = false;
      resize.disconnect();
      mixer.stopAllAction();
      canvas.remove();
      environment.dispose();
      root.traverse((n) => {
        if (!(n instanceof THREE.Mesh)) return;
        n.geometry.dispose();
        for (const m of Array.isArray(n.material) ? n.material : [n.material]) {
          for (const value of Object.values(m)) if (value instanceof THREE.Texture) value.dispose();
          m.dispose();
        }
      });
      r.renderLists.dispose();
    };
    return probe;
  };

  return {
    play,
    hide,
    setVisible: (on: boolean): void => {
      visible = on;
      applyVisible();
    },
    probe: (): ProofStageProbe => probe,
  };
}
